import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, Sun, Moon, LogOut, Settings, Menu, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAlerts } from '../../contexts/AlertsContext';
import { findActiveNavItem } from '../../lib/navigation';
import { formatHeaderDate } from '../../lib/datetime';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface HeaderProps {
  isDark: boolean;
  setIsDark: (value: boolean) => void;
  onMobileMenuToggle: () => void;
  mobileMenuOpen?: boolean;
}

/** Квадратная кнопка 40px с рамкой — кнопки шапки по макету. */
const headerButton =
  'relative grid size-10 shrink-0 place-items-center rounded-xl border border-border bg-surface text-text outline-none transition-colors hover:bg-surface-alt focus-visible:ring-2 focus-visible:ring-ring';

/** Дата в шапке обновляется раз в минуту (смена дня в полночь по Ташкенту). */
function useHeaderDate() {
  const [label, setLabel] = useState(() => formatHeaderDate());
  useEffect(() => {
    const id = window.setInterval(() => setLabel(formatHeaderDate()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return label;
}

export default function Header({ isDark, setIsDark, onMobileMenuToggle, mobileMenuOpen }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { suspectedDuplicates, error: alertsError, reload: reloadAlerts } = useAlerts();
  const [alertsOpen, setAlertsOpen] = useState(false);
  const dateLabel = useHeaderDate();
  const pageTitle = findActiveNavItem(location.pathname)?.label ?? 'Админ-панель';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const displayName =
    user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.username || 'Админ';
  const displayEmail = user?.email || user?.username || '';
  const initial = (displayName.trim()[0] ?? 'A').toUpperCase();

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-4 border-b border-border-soft bg-surface px-4 lg:px-7">
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={onMobileMenuToggle}
          aria-label="Открыть меню навигации"
          aria-expanded={mobileMenuOpen}
          className={cn(headerButton, 'lg:hidden')}
        >
          <Menu className="size-[18px]" />
        </button>
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-xs text-text-2">{dateLabel} · Ташкент</span>
          <b className="truncate font-display text-lg">{pageTitle}</b>
        </div>
      </div>

      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={() => setIsDark(!isDark)}
          aria-label={isDark ? 'Включить светлую тему' : 'Включить тёмную тему'}
          className={headerButton}
        >
          {isDark ? <Sun className="size-[18px]" strokeWidth={1.8} /> : <Moon className="size-[18px]" strokeWidth={1.8} />}
        </button>

        <Popover
          open={alertsOpen}
          onOpenChange={(open) => {
            setAlertsOpen(open);
            if (open) reloadAlerts();
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={suspectedDuplicates ? `События: ${suspectedDuplicates} возможных дублей` : 'События'}
              className={headerButton}
            >
              <Bell className="size-[18px]" strokeWidth={1.8} />
              {!!suspectedDuplicates && (
                <span aria-hidden className="absolute right-[9px] top-2 size-2 rounded-full bg-neg" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="end">
            <div className="border-b border-border-soft px-4 py-3">
              <h3 className="font-display text-[15px] font-bold">Требует внимания</h3>
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
              {alertsError ? (
                <p className="px-3 py-6 text-center text-sm text-text-2">
                  Не удалось загрузить события.{' '}
                  <button type="button" onClick={reloadAlerts} className="font-semibold text-accent-text underline">
                    Повторить
                  </button>
                </p>
              ) : suspectedDuplicates === null ? (
                <p className="px-3 py-6 text-center text-sm text-text-2">Загрузка…</p>
              ) : suspectedDuplicates === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-text-2">Нет новых событий</p>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setAlertsOpen(false);
                    navigate('/duplicates');
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left outline-none hover:bg-muted focus-visible:bg-muted"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">Возможные дубли аккаунтов</span>
                    <span className="mt-0.5 block text-xs text-text-2">
                      Ждут решения: {suspectedDuplicates}. Открыть раздел «Дубли аккаунтов»
                    </span>
                  </span>
                  <ChevronRight className="size-4 shrink-0 text-text-2" aria-hidden />
                </button>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Меню профиля: ${displayName}`}
              className="flex h-10 items-center gap-2 rounded-xl border border-border bg-surface pl-1.5 pr-3 text-text outline-none transition-colors hover:bg-surface-alt focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="grid size-7 place-items-center rounded-full bg-inverse text-xs font-bold text-on-inverse">
                {initial}
              </span>
              <span className="hidden max-w-40 truncate font-semibold sm:block">{displayName}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60" align="end">
            <div className="border-b border-border-soft px-2.5 pb-2 pt-1.5">
              <p className="truncate text-sm font-semibold">{displayName}</p>
              <p className="truncate text-xs text-text-2">{displayEmail || 'Администратор'}</p>
            </div>
            <DropdownMenuItem onClick={() => navigate('/settings')} className="mt-1">
              <Settings />
              Настройки
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive onClick={handleLogout}>
              <LogOut />
              Выйти
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
